import { Component, ContentChild, Directive, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { DomAttributesService } from '@backbase/ui-ang/services';

/**
 * Directive for custom modal header content template.
 *
 * @remarks
 * Allows embedding custom content within modal header using Angular's content projection.
 * The directive wraps template content and provides access to the template reference for rendering.
 *
 * @example
 *
 * ```html
 * <bb-modal-header-ui>
 *   <ng-template bbCustomModalHeader>
 *     Custom header with logo and title
 *   </ng-template>
 * </bb-modal-header-ui>
 * ```
 */
@Directive({
  selector: 'ng-template[bbCustomModalHeader]',
  standalone: false,
})
export class CustomModalHeaderDirective {
  constructor(public templateRef: TemplateRef<any>) {}
}

/**
 * Modal header component that provides title display and close functionality for modal dialogs.
 *
 * @remarks
 * Handles modal header with customizable title, close button, and accessibility features.
 * Automatically generates unique IDs for proper aria-labelledby relationships.
 * Supports custom header templates through content projection.
 *
 * @example
 *
 * ```html
 * <bb-modal-header-ui title="Cookies on Backbase"></bb-modal-header-ui>
 * ```
 */
@Component({
  selector: 'bb-modal-header-ui',
  templateUrl: './modal-header.component.html',
  standalone: false,
})
export class ModalHeaderComponent {
  /**
   * Custom modal header directive containing user-provided template content.
   */
  @ContentChild(CustomModalHeaderDirective, { static: true }) customModalHeader: CustomModalHeaderDirective | undefined;

  /**
   * Reference to this component instance for template context.
   */
  readonly hostRef: ModalHeaderComponent = this;
  /**
   * Title text for the modal header. Defaults to empty string.
   *
   * @example
   *
   * ```html
   * <bb-modal-header-ui title="Cookies on Backbase"></bb-modal-header-ui>
   * ```
   */
  @Input() title = '';

  /**
   * Aria label for a "close" button
   *
   * @example
   *
   * ```html
   * <bb-modal-header-ui closeButtonAriaLabel="Close dialog"></bb-modal-header-ui>
   * ```
   */
  @Input() closeButtonAriaLabel = $localize`:@@bb-modal-header-ui.close-button.aria-label:Close modal window`;

  /**
   * Whether to display the close button.
   * Defaults to true.
   *
   * @example
   *
   * ```html
   * <bb-modal-header-ui [showClose]="false"></bb-modal-header-ui>
   * ```
   */
  @Input() showClose = true;

  /**
   * The event emitted when the modal header close button is clicked.
   *
   * @example
   *
   * ```html
   * <bb-modal-header-ui (close)="onModalClose()"></bb-modal-header-ui>
   * ```
   */
  @Output() close = new EventEmitter();

  /**
   * Unique identifier for the modal header element.
   *
   * @example
   *
   * ```html
   * <h2 [id]="headerId" class="modal-title">
   * ```
   */
  public readonly headerId = this.domAttrService.generateId();

  /**
   * Creates modal header component instance.
   *
   * @remarks
   * Initializes the component with DOM attributes service for generating unique IDs.
   * The service is used to create accessible header elements with proper identification.
   *
   * @param domAttrService - Service for generating unique DOM element IDs
   *
   * @example
   *
   * ```typescript
   * // Constructor is called automatically by Angular
   * constructor(private readonly domAttrService: DomAttributesService) {}
   * ```
   */
  constructor(private readonly domAttrService: DomAttributesService) {}

  /**
   * Handles close button click by emitting close event.
   *
   * @remarks
   * Triggers the close event that can be handled by parent components.
   * Used internally by the modal header's close button click handler.
   *
   * @example
   *
   * ```html
   * <button (click)="onClose()" class="btn-close"></button>
   * ```
   */
  onClose() {
    this.close.emit();
  }
}
